/**
 * FocusTimeDialog
 *
 * Quick-access dialog for managing focus time blocks.
 * Opened from the toolbar Target icon. Lets users toggle focus time,
 * generate/regenerate/clear blocks, and see their current configuration.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Loader2, Settings } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import type { FocusTimeSettingsDetail, FocusTimeSettingsInput } from '@/lib/api';
import type { TimeFormat } from '../types';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const TIME_OF_DAY_LABELS: Record<string, string> = {
  morning: 'Morning',
  afternoon: 'Afternoon',
  evening: 'Evening',
};

function formatHour(hour: number, format: TimeFormat): string {
  if (format === '24h') return `${String(hour).padStart(2, '0')}:00`;
  if (hour === 0) return '12 AM';
  if (hour < 12) return `${hour} AM`;
  if (hour === 12) return '12 PM';
  return `${hour - 12} PM`;
}

interface FocusTimeDialogProps {
  open: boolean;
  onClose: () => void;
  focusTimeSettings: FocusTimeSettingsDetail | null;
  onUpdateFocusTime: (input: FocusTimeSettingsInput) => Promise<unknown>;
  onGenerate: () => Promise<number>;
  onRegenerate: () => Promise<number>;
  onClear: () => Promise<number>;
  onOpenSettings: () => void;
  timeFormat?: TimeFormat;
}

export function FocusTimeDialog({
  open,
  onClose,
  focusTimeSettings,
  onUpdateFocusTime,
  onGenerate,
  onRegenerate,
  onClear,
  onOpenSettings,
  timeFormat = '12h',
}: FocusTimeDialogProps) {
  const [loading, setLoading] = useState(false);

  const handleAction = async (action: () => Promise<number>, label: string) => {
    setLoading(true);
    try {
      const count = await action();
      toast({ title: `${label}: ${count} block${count === 1 ? '' : 's'}` });
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : `Failed to ${label.toLowerCase()}`,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (enabled: boolean) => {
    try {
      await onUpdateFocusTime({ isEnabled: enabled });
      toast({ title: enabled ? 'Focus time enabled' : 'Focus time disabled' });
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update',
        variant: 'destructive',
      });
    }
  };

  const isEnabled = focusTimeSettings?.isEnabled ?? false;
  const preferredTime = focusTimeSettings?.preferredTimeOfDay ?? 'morning';
  const preferredDays = focusTimeSettings?.preferredDays ?? [1, 2, 3, 4, 5];
  const startHour = focusTimeSettings?.preferredStartHour ?? 9;
  const endHour = focusTimeSettings?.preferredEndHour ?? 12;
  const minBlock = focusTimeSettings?.minBlockMinutes ?? 60;
  const maxBlock = focusTimeSettings?.maxBlockMinutes ?? 120;
  const daysPerWeek = focusTimeSettings?.daysPerWeek ?? 3;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Focus Time</DialogTitle>
          <DialogDescription>Block distraction-free time on your calendar.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Enable toggle */}
          <div className="flex items-center justify-between gap-4 rounded-md border px-3 py-2.5">
            <span className="text-sm font-medium">Enable focus time</span>
            <Switch checked={isEnabled} onCheckedChange={handleToggle} className="shrink-0" />
          </div>

          {/* Current configuration summary */}
          {focusTimeSettings && (
            <div className="rounded-md border px-3 py-2.5 space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Current schedule
              </p>
              <div className="text-sm space-y-1">
                <p>
                  {TIME_OF_DAY_LABELS[preferredTime]} &middot; {formatHour(startHour, timeFormat)}–
                  {formatHour(endHour, timeFormat)}
                </p>
                <p>
                  {minBlock}–{maxBlock} min blocks &middot; {daysPerWeek}×/week
                </p>
                <p className="text-muted-foreground">
                  {preferredDays.map((d) => DAY_LABELS[d]).join(', ')}
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={loading || !isEnabled}
              onClick={() => handleAction(onGenerate, 'Generated')}
            >
              {loading && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              Generate Blocks
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={loading || !isEnabled}
              onClick={() => handleAction(onRegenerate, 'Regenerated')}
            >
              Regenerate
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={loading}
              onClick={() => handleAction(onClear, 'Cleared')}
            >
              Clear All
            </Button>
          </div>

          {/* Link to full settings */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-muted-foreground"
            onClick={() => {
              onClose();
              onOpenSettings();
            }}
          >
            <Settings className="h-3.5 w-3.5 mr-1.5" />
            Full settings
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
